import {
  DISTRIBUTE_FEED_ITEM_RENDER_ROOT_MARGIN,
  DISTRIBUTE_FEED_LOAD_MORE_ROOT_MARGIN,
  DISTRIBUTE_FEED_PRIORITY_ITEM_COUNT,
  DISTRIBUTE_MEDIA_PRELOAD_ROOT_MARGIN,
} from '@/constants/distribute'

export type RuntimePerformanceTier = 'standard' | 'constrained'

export interface RuntimePerformanceBudget {
  deviceMemory: number | null
  enableGalleryAutoplay: boolean
  enableMediaAutoplay: boolean
  feedItemRenderRootMargin: string
  feedLoadMoreRootMargin: string
  feedPriorityItemCount: number
  hardwareConcurrency: number | null
  isConstrained: boolean
  mediaPreloadRootMargin: string
  prefersReducedMotion: boolean
  saveData: boolean
  tier: RuntimePerformanceTier
}

type NavigatorConnectionLike = {
  saveData?: boolean
}

type NavigatorPerformanceLike = Navigator & {
  connection?: NavigatorConnectionLike
  deviceMemory?: number
  mozConnection?: NavigatorConnectionLike
  webkitConnection?: NavigatorConnectionLike
}

const CONSTRAINED_FEED_LOAD_MORE_ROOT_MARGIN = '480px 0px'
const CONSTRAINED_FEED_ITEM_RENDER_ROOT_MARGIN = '420px 0px'
const CONSTRAINED_MEDIA_PRELOAD_ROOT_MARGIN = '160px 0px'
const CONSTRAINED_FEED_PRIORITY_ITEM_COUNT = 1

let cachedBudget: RuntimePerformanceBudget | null = null

function readNavigator(): NavigatorPerformanceLike | null {
  if (typeof navigator === 'undefined') {
    return null
  }

  return navigator as NavigatorPerformanceLike
}

function readConnectionSaveData(nav: NavigatorPerformanceLike | null) {
  const connection = nav?.connection ?? nav?.mozConnection ?? nav?.webkitConnection

  return Boolean(connection?.saveData)
}

function readPrefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }

  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function resolveRuntimePerformanceBudget(): RuntimePerformanceBudget {
  const nav = readNavigator()
  const hardwareConcurrency =
    typeof nav?.hardwareConcurrency === 'number' && Number.isFinite(nav.hardwareConcurrency)
      ? nav.hardwareConcurrency
      : null
  const deviceMemory =
    typeof nav?.deviceMemory === 'number' && Number.isFinite(nav.deviceMemory)
      ? nav.deviceMemory
      : null
  const saveData = readConnectionSaveData(nav)
  const prefersReducedMotion = readPrefersReducedMotion()
  const isConstrained =
    saveData ||
    prefersReducedMotion ||
    (hardwareConcurrency !== null && hardwareConcurrency <= 4) ||
    (deviceMemory !== null && deviceMemory <= 4)
  const tier: RuntimePerformanceTier = isConstrained ? 'constrained' : 'standard'

  return {
    deviceMemory,
    enableGalleryAutoplay: false,
    enableMediaAutoplay: false,
    feedItemRenderRootMargin: isConstrained
      ? CONSTRAINED_FEED_ITEM_RENDER_ROOT_MARGIN
      : DISTRIBUTE_FEED_ITEM_RENDER_ROOT_MARGIN,
    feedLoadMoreRootMargin: isConstrained
      ? CONSTRAINED_FEED_LOAD_MORE_ROOT_MARGIN
      : DISTRIBUTE_FEED_LOAD_MORE_ROOT_MARGIN,
    feedPriorityItemCount: isConstrained
      ? CONSTRAINED_FEED_PRIORITY_ITEM_COUNT
      : DISTRIBUTE_FEED_PRIORITY_ITEM_COUNT,
    hardwareConcurrency,
    isConstrained,
    mediaPreloadRootMargin: isConstrained
      ? CONSTRAINED_MEDIA_PRELOAD_ROOT_MARGIN
      : DISTRIBUTE_MEDIA_PRELOAD_ROOT_MARGIN,
    prefersReducedMotion,
    saveData,
    tier,
  }
}

export function getRuntimePerformanceBudget() {
  if (!cachedBudget) {
    cachedBudget = resolveRuntimePerformanceBudget()
  }

  return cachedBudget
}

export function resetRuntimePerformanceBudgetForTest() {
  cachedBudget = null
}
